import React, { useEffect, useState } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Users,
  MessageSquare,
  Edit,
  MoreVertical,
  Link as LinkIcon,
  Twitter,
  Linkedin,
  Instagram,
  Check,
  CheckCircle,
  Flag,
  Store,
  Award,
  Shield,
  Briefcase
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';
import PostCard from '../../components/community/PostCard';
import FollowButton from '../../components/community/FollowButton';
import useReportStore from '../../store/reportStore';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import communityAPI from '../../services/community.api';

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('posts');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [stats, setStats] = useState({
    postsCount: 0,
    followersCount: 0,
    followingCount: 0
  });

  const {
    getUserPosts,
    getFollowers,
    getFollowing,
    sendMessage
  } = useCommunityStore();
  
  const { user: currentUser } = useAuthStore();
  const { openReportModal } = useReportStore();

  useEffect(() => {
    if (userId) {
      loadUserProfile();
    }
  }, [userId]);

  const loadUserProfile = async () => {
    setIsLoading(true);
    try {
      const profileResponse = await communityAPI.getUserProfile(userId);
      if (!profileResponse.success) {
        throw new Error(profileResponse.message || 'User not found');
      }
      
      const userProfile = profileResponse.data;
      setUserData(userProfile);
      
      const postsResponse = await getUserPosts(userId, { page: 1, limit: 10 });
      setUserPosts(postsResponse.posts || []);
      
      const followersResponse = await getFollowers(userId);
      setFollowers(followersResponse.followers || []);
      
      const followingResponse = await getFollowing(userId);
      setFollowing(followingResponse.following || []);
      
      setStats({
        postsCount: userProfile.postsCount || 0,
        followersCount: userProfile.followersCount || 0,
        followingCount: userProfile.followingCount || 0
      });
      
    } catch (error) {
      console.error('Failed to load user profile:', error);
      toast.error(error.message || 'Failed to load user profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!currentUser) {
      toast.error('Please login to send messages');
      navigate('/login');
      return;
    }

    try {
      navigate('/community?tab=messages&user=' + userId, {
        state: { 
          openChat: true, 
          selectedUserId: userId,
          userName: `${userData.firstName} ${userData.lastName}`
        }
      });
    } catch (error) {
      console.error('Navigation error:', error);
      toast.error('Failed to open chat');
    }
  };

  const handleReportUser = () => {
    openReportModal({
      type: 'user',
      id: userId,
      data: {
        user: userData
      }
    });
  };

  const tabs = [
    { id: 'posts', name: 'Posts', count: stats?.postsCount || 0, icon: Briefcase },
    { id: 'followers', name: 'Followers', count: stats?.followersCount || 0, icon: Users },
    { id: 'following', name: 'Following', count: stats?.followingCount || 0, icon: Users },
    { id: 'about', name: 'About', count: null, icon: User },
  ];

  const getSocialLinks = () => {
    if (!userData?.socialLinks) return {};
    
    try {
      if (typeof userData.socialLinks === 'string') {
        return JSON.parse(userData.socialLinks);
      }
      return userData.socialLinks;
    } catch (error) {
      console.error('Error parsing social links:', error);
      return {};
    }
  };

  const socialLinks = getSocialLinks();

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-4 sm:py-6 md:py-8 px-3 sm:px-4 pb-20 sm:pb-8">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              <div className="h-24 w-24 sm:h-32 sm:w-32 md:h-40 md:w-40 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 mx-auto sm:mx-0"></div>
              <div className="flex-1 space-y-3 sm:space-y-4">
                <div className="h-6 sm:h-8 bg-gray-200 rounded w-36 sm:w-48"></div>
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-24 sm:w-32"></div>
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-48 sm:w-64"></div>
                <div className="flex flex-wrap gap-2">
                  <div className="h-8 sm:h-10 bg-gray-200 rounded w-24 sm:w-32"></div>
                  <div className="h-8 sm:h-10 bg-gray-200 rounded w-24 sm:w-32"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="max-w-4xl mx-auto py-8 sm:py-12 px-3 sm:px-4 pb-20 sm:pb-12">
        <div className="bg-white rounded-xl shadow-neomorph p-6 sm:p-8 text-center border border-primary-100/30">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-50 shadow-neomorph-inset mb-3 sm:mb-4">
            <User className="h-8 w-8 sm:h-10 sm:w-10 text-red-400" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">User not found</h2>
          <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">The user profile you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/community')}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
          >
            Back to FastConnect
          </button>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === userId;

  return (
    <div className="max-w-4xl mx-auto py-4 sm:py-6 md:py-8 px-3 sm:px-4 pb-20 sm:pb-8">
      {/* Profile Header */}
      <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          {/* Avatar */}
          <div className="flex-shrink-0 mx-auto sm:mx-0">
            <div className="h-24 w-24 sm:h-28 sm:w-28 md:h-36 md:w-36 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 flex items-center justify-center overflow-hidden">
              {userData.avatar ? (
                <img 
                  src={userData.avatar} 
                  alt={`${userData.firstName} ${userData.lastName}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-white text-3xl sm:text-4xl md:text-5xl font-bold">
                  {userData.firstName?.[0]}{userData.lastName?.[0]}
                </span>
              )}
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
              <div>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3 mb-2">
                  <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800">
                    {userData.firstName} {userData.lastName}
                  </h1>
                  {userData.phoneVerified && (
                    <span className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 bg-green-100 text-green-700 text-[9px] sm:text-xs font-medium rounded-lg">
                      <CheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      Verified
                    </span>
                  )}
                </div>
                
                <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">
                  {userData.bio || 'No bio provided'}
                </p>

                {/* Business Tags */}
                {userData.businessTags && userData.businessTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 justify-center sm:justify-start">
                    {Array.isArray(userData.businessTags) ? (
                      userData.businessTags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-1.5 sm:px-3 py-0.5 sm:py-1.5 bg-primary-50 text-primary-700 text-[10px] sm:text-sm font-medium rounded-lg border border-primary-200"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="px-1.5 sm:px-3 py-0.5 sm:py-1.5 bg-primary-50 text-primary-700 text-[10px] sm:text-sm font-medium rounded-lg border border-primary-200">
                        {userData.businessTags}
                      </span>
                    )}
                    {userData.businessTags.length > 3 && (
                      <span className="px-1.5 sm:px-3 py-0.5 sm:py-1.5 bg-gray-50 text-gray-600 text-[10px] sm:text-sm font-medium rounded-lg">
                        +{userData.businessTags.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Details */}
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 items-center sm:items-start">
                  {userData.location && (
                    <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                      <span className="text-xs sm:text-sm">{userData.location}</span>
                    </div>
                  )}
                  
                  {userData.website && (
                    <div className="flex items-center gap-1.5 sm:gap-2 text-primary-600">
                      <LinkIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                      <a 
                        href={userData.website.includes('://') ? userData.website : `https://${userData.website}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs sm:text-sm hover:underline truncate max-w-[180px] sm:max-w-none"
                      >
                        {userData.website.replace(/https?:\/\//, '').substring(0, 30)}
                      </a>
                    </div>
                  )}
                  
                  {userData.createdAt && (
                    <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                      <span className="text-xs sm:text-sm">
                        Joined {formatDistanceToNow(new Date(userData.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 justify-center sm:justify-end">
                {!isOwnProfile ? (
                  <>
                    <FollowButton 
                      userId={userId}
                      size="lg"
                      showCount={true}
                      onFollowChange={(following) => {
                        setStats(prev => ({
                          ...prev,
                          followersCount: following ? prev.followersCount + 1 : Math.max(0, prev.followersCount - 1)
                        }));
                      }}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs sm:text-sm"
                    />
                    
                    <button
                      onClick={handleSendMessage}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                    >
                      <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Message
                    </button>
                    
                    <button
                      onClick={handleReportUser}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white text-red-600 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover hover:bg-red-50 transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                    >
                      <Flag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Report
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/marketplace/businesses')}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg shadow-md shadow-blue-500/30 hover:shadow-lg transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                    >
                      <Store className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      My Businesses
                    </button>
                    
                    <button
                      onClick={() => navigate('/settings')}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                    >
                      <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Edit Profile
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-4 sm:pt-6 mt-3 sm:mt-4 border-t border-primary-100/50">
              <div className="text-center">
                <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-800">{stats.postsCount}</div>
                <div className="text-[10px] sm:text-sm text-gray-500">Posts</div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-800">{stats.followersCount}</div>
                <div className="text-[10px] sm:text-sm text-gray-500">Followers</div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-xl md:text-2xl font-bold text-gray-800">{stats.followingCount}</div>
                <div className="text-[10px] sm:text-sm text-gray-500">Following</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs - Horizontal scroll on mobile */}
      <div className="bg-white rounded-xl shadow-neomorph p-1 sm:p-2 border border-primary-100/30 mb-4 sm:mb-6 overflow-x-auto">
        <div className="flex flex-nowrap min-w-max sm:min-w-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center px-2.5 sm:px-4 py-1.5 sm:py-3 rounded-lg font-medium text-[11px] sm:text-sm transition-all mr-1 whitespace-nowrap flex-shrink-0
                ${activeTab === tab.id
                  ? 'bg-primary-50 text-primary-600 shadow-neomorph-inset border border-primary-200'
                  : 'text-gray-600 hover:bg-gray-50'
                }
              `}
            >
              <div className={`p-0.5 sm:p-1.5 rounded-md mr-1 sm:mr-2 ${
                activeTab === tab.id ? 'bg-primary-100' : 'bg-gray-100'
              }`}>
                <tab.icon className={`h-3 w-3 sm:h-4 sm:w-4 ${
                  activeTab === tab.id ? 'text-primary-600' : 'text-gray-500'
                }`} />
              </div>
              {tab.name}
              {tab.count !== null && (
                <span className={`
                  ml-1 sm:ml-2 px-1 sm:px-2 py-0.5 text-[9px] sm:text-xs rounded-full
                  ${activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-700'
                  }
                `}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div>
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2 sm:mr-3" />
              <h2 className="text-sm sm:text-lg font-semibold text-gray-800">Recent Posts</h2>
            </div>
            
            {userPosts.length > 0 ? (
              <div className="space-y-4 sm:space-y-6">
                {userPosts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-primary-50 shadow-neomorph-inset mb-3 sm:mb-4">
                  <MessageSquare className="h-7 w-7 sm:h-10 sm:w-10 text-primary-400" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-1 sm:mb-2">No posts yet</h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  {isOwnProfile 
                    ? 'Share your first post with the community!'
                    : `${userData.firstName} hasn't posted anything yet.`
                  }
                </p>
                {isOwnProfile && (
                  <button
                    onClick={() => navigate('/community')}
                    className="mt-3 sm:mt-4 px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
                  >
                    Create Your First Post
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Followers Tab */}
        {activeTab === 'followers' && (
          <div>
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-2 sm:mr-3" />
              <h2 className="text-sm sm:text-lg font-semibold text-gray-800">Followers</h2>
            </div>
            
            {followers.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {followers.map((follower) => (
                  <div 
                    key={follower.id}
                    className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all border border-transparent hover:border-gray-200 cursor-pointer"
                    onClick={() => navigate(`/community/profile/${follower.id}`)}
                  >
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                      {follower.avatar ? (
                        <img 
                          src={follower.avatar} 
                          alt={follower.firstName}
                          className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl object-cover"
                        />
                      ) : (
                        <span className="text-white font-semibold text-sm sm:text-base">
                          {follower.firstName?.[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 text-xs sm:text-sm truncate">
                        {follower.firstName} {follower.lastName}
                      </h4>
                      {follower.businessTags && follower.businessTags.length > 0 && (
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                          {Array.isArray(follower.businessTags) 
                            ? follower.businessTags[0]
                            : follower.businessTags
                          }
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-blue-50 shadow-neomorph-inset mb-3 sm:mb-4">
                  <Users className="h-7 w-7 sm:h-10 sm:w-10 text-blue-400" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-1 sm:mb-2">No followers yet</h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  {isOwnProfile
                    ? 'Share posts and connect with others to gain followers!'
                    : `${userData.firstName} doesn't have any followers yet.`
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* Following Tab */}
        {activeTab === 'following' && (
          <div>
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-2 sm:mr-3" />
              <h2 className="text-sm sm:text-lg font-semibold text-gray-800">Following</h2>
            </div>
            
            {following.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {following.map((followed) => (
                  <div 
                    key={followed.id}
                    className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all border border-transparent hover:border-gray-200 cursor-pointer"
                    onClick={() => navigate(`/community/profile/${followed.id}`)}
                  >
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                      {followed.avatar ? (
                        <img 
                          src={followed.avatar} 
                          alt={followed.firstName}
                          className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl object-cover"
                        />
                      ) : (
                        <span className="text-white font-semibold text-sm sm:text-base">
                          {followed.firstName?.[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 text-xs sm:text-sm truncate">
                        {followed.firstName} {followed.lastName}
                      </h4>
                      {followed.businessTags && followed.businessTags.length > 0 && (
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                          {Array.isArray(followed.businessTags)
                            ? followed.businessTags[0]
                            : followed.businessTags
                          }
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-purple-50 shadow-neomorph-inset mb-3 sm:mb-4">
                  <Users className="h-7 w-7 sm:h-10 sm:w-10 text-purple-400" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-1 sm:mb-2">Not following anyone yet</h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  {isOwnProfile
                    ? 'Discover and connect with other business professionals!'
                    : `${userData.firstName} isn't following anyone yet.`
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div>
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2 sm:mr-3" />
              <h2 className="text-sm sm:text-lg font-semibold text-gray-800">About</h2>
            </div>
            
            <div className="space-y-4 sm:space-y-6">
              {/* Bio */}
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2 flex items-center">
                  <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                  Bio
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-line">
                  {userData.bio || 'No bio provided'}
                </p>
              </div>

              {/* Contact Info */}
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 flex items-center">
                  <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-1.5 sm:mr-2" />
                  Contact Information
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  {userData.email && (
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700 break-words">{userData.email}</span>
                      {userData.emailVerified && (
                        <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-100 text-green-700 text-[9px] sm:text-xs font-medium rounded-lg">
                          <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          Verified
                        </span>
                      )}
                    </div>
                  )}
                  
                  {userData.phone && (
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700">{userData.phone}</span>
                      {userData.phoneVerified && (
                        <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-100 text-green-700 text-[9px] sm:text-xs font-medium rounded-lg">
                          <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          Verified
                        </span>
                      )}
                    </div>
                  )}
                  
                  {userData.location && (
                    <div className="flex items-center gap-2 sm:gap-3">
                      <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700">{userData.location}</span>
                    </div>
                  )}
                  
                  {userData.website && (
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <LinkIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      <a 
                        href={userData.website.includes('://') ? userData.website : `https://${userData.website}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:underline text-xs sm:text-sm break-words"
                      >
                        {userData.website.replace(/https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Links */}
              {(socialLinks.twitter || socialLinks.linkedin || socialLinks.instagram) && (
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 flex items-center">
                    <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-1.5 sm:mr-2" />
                    Social Links
                  </h3>
                  <div className="flex gap-3 sm:gap-4">
                    {socialLinks.twitter && (
                      <a 
                        href={socialLinks.twitter.includes('://') ? socialLinks.twitter : `https://twitter.com/${socialLinks.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 sm:p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all shadow-neomorph-inset"
                        title="Twitter"
                      >
                        <Twitter className="h-4 w-4 sm:h-5 sm:w-5" />
                      </a>
                    )}
                    
                    {socialLinks.linkedin && (
                      <a 
                        href={socialLinks.linkedin.includes('://') ? socialLinks.linkedin : `https://linkedin.com/in/${socialLinks.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 sm:p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-all shadow-neomorph-inset"
                        title="LinkedIn"
                      >
                        <Linkedin className="h-4 w-4 sm:h-5 sm:w-5" />
                      </a>
                    )}
                    
                    {socialLinks.instagram && (
                      <a 
                        href={socialLinks.instagram.includes('://') ? socialLinks.instagram : `https://instagram.com/${socialLinks.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 sm:p-3 bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 transition-all shadow-neomorph-inset"
                        title="Instagram"
                      >
                        <Instagram className="h-4 w-4 sm:h-5 sm:w-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Account Status */}
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 flex items-center">
                  <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full mr-1.5 sm:mr-2" />
                  Account Status
                </h3>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <div className={`p-1 sm:p-1.5 rounded-lg ${userData.phoneVerified ? 'bg-green-100' : 'bg-yellow-100'}`}>
                      <Shield className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${userData.phoneVerified ? 'text-green-600' : 'text-yellow-600'}`} />
                    </div>
                    <span className="text-[10px] sm:text-sm text-gray-600">
                      {userData.phoneVerified ? 'Verified Account' : 'Unverified Account'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-1">
                    <div className="p-1 sm:p-1.5 bg-purple-100 rounded-lg">
                      <Award className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600" />
                    </div>
                    <span className="text-[10px] sm:text-sm text-gray-600">
                      {userData.tier || 'Standard Member'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfilePage;